import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Globe, Search } from 'lucide-react';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPanel,
} from './ModalShell';

/**
 * Pick an IANA timezone from the full list the runtime knows about. Standalone on
 * purpose — a zone is edited from more than one screen (automations today, scheduled
 * sends and calendars next), and a modal buried in a feature view cannot be reused from
 * the next one.
 */

/** Shown first, above the full list — the zones actually used day to day. */
const COMMON_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Atlantic/Reykjavik',
  'UTC',
];

/**
 * `Intl.supportedValuesOf` is ES2022 and this project's lib is ES2020, so it is not on
 * the ambient `Intl` type. Every browser we support ships it; the fallback is for a
 * stale runtime, not for correctness.
 */
function loadZones(): string[] {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  try {
    const zones = intl.supportedValuesOf?.('timeZone');
    if (zones?.length) return zones;
  } catch {
    // fall through
  }
  return COMMON_ZONES;
}

/** ~450 zones, and the offsets need a formatter each — do it once per tab, not per open. */
let zoneCache: { zone: string; offset: string; haystack: string }[] | null = null;

function offsetFor(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

function allZones() {
  if (!zoneCache) {
    zoneCache = loadZones().map((zone) => ({
      zone,
      offset: offsetFor(zone),
      haystack: zone.toLowerCase().replace(/[_/]/g, ' '),
    }));
  }
  return zoneCache;
}

export interface TimezoneModalProps {
  isOpen: boolean;
  /** Zone currently in force; also what the draft resets to on each open. */
  value: string;
  onClose: () => void;
  onSelect: (tz: string) => void;
  /** Shown under the search box — say what moves when the zone changes. */
  description?: string;
}

export default function TimezoneModal({
  isOpen,
  value,
  onClose,
  onSelect,
  description,
}: TimezoneModalProps) {
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(value);
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPending(value);
    setQuery('');
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Open on the current zone rather than at the top of a 450-row list.
  useEffect(() => {
    if (!isOpen) return;
    const id = window.setTimeout(
      () => selectedRef.current?.scrollIntoView({ block: 'center' }),
      0,
    );
    return () => window.clearTimeout(id);
  }, [isOpen]);

  const groups = useMemo(() => {
    const zones = allZones();
    const q = query.trim().toLowerCase().replace(/[_/]/g, ' ');

    // A zone stored before it left the IANA list still has to be visible and keepable.
    const known = zones.some((z) => z.zone === value);
    const withCurrent = known
      ? zones
      : [{ zone: value, offset: offsetFor(value), haystack: value.toLowerCase() }, ...zones];

    if (q) {
      const hits = withCurrent.filter((z) => z.haystack.includes(q));
      return [{ label: `${hits.length} match${hits.length === 1 ? '' : 'es'}`, zones: hits }];
    }

    const commonSet = new Set(COMMON_ZONES);
    return [
      { label: 'Common', zones: withCurrent.filter((z) => commonSet.has(z.zone)) },
      { label: 'All timezones', zones: withCurrent.filter((z) => !commonSet.has(z.zone)) },
    ];
  }, [query, value]);

  if (!isOpen) return null;

  // No backdrop close — a stray click outside must not discard a staged zone.
  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel maxWidthClass="max-w-md" titleId="timezone-modal-title">
        <ModalHeader
          title="Timezone"
          titleId="timezone-modal-title"
          icon={<Globe className="w-5 h-5 text-emerald-500" />}
          onClose={onClose}
        />
        <div className="shrink-0 px-6 pt-4 space-y-2">
          {description ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>
          ) : null}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Search city or region…"
              aria-label="Search timezones"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
        <ModalBody className="sm:max-h-[45vh] pt-3">
          {groups.map((group) =>
            group.zones.length === 0 ? null : (
              <div key={group.label} className="mb-3 last:mb-0">
                <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.zones.map(({ zone, offset }) => {
                    const active = pending === zone;
                    return (
                      <button
                        key={zone}
                        type="button"
                        ref={zone === value ? selectedRef : undefined}
                        onClick={() => setPending(zone)}
                        onDoubleClick={() => {
                          onSelect(zone);
                          onClose();
                        }}
                        aria-pressed={active}
                        className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-1.5 text-left transition-colors ${
                          active
                            ? 'bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-500'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'
                        }`}
                      >
                        <span className="text-sm font-mono text-gray-900 dark:text-gray-100 truncate">
                          {zone}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          {offset ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {offset}
                            </span>
                          ) : null}
                          {active ? (
                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
          )}
          {groups.every((g) => g.zones.length === 0) ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No timezone matches “{query.trim()}”.
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSelect(pending);
              onClose();
            }}
            disabled={pending === value}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            Use this zone
          </button>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
}
